import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import apiClient from '../../utils/api';

const ROLES = ['COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT', 'INDEPENDENT_USER', 'SUPER_ADMIN', 'TRANSPORTER', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'];
const EMPTY = { firstName: '', lastName: '', email: '', password: '', phone: '', role: 'COMPANY_ADMIN', companyId: '', branchId: '', status: 'ACTIVE' };

export const SuperAdminUserForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({ ...EMPTY, companyId: searchParams.get('companyId') ?? '' });
  const [companies, setCompanies] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.listCompanies(100, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
    if (isEdit) {
      apiClient.listUsers(undefined, 1000, 0).then(r => {
        const d = r.data.data;
        const users = d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []);
        const u = users.find((x: any) => x.id === id);
        if (u) setForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, password: '', phone: u.phone ?? '', role: u.role, companyId: u.companyId ?? '', branchId: u.branchId ?? '', status: u.status });
      }).catch(() => {});
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (form.companyId) {
      apiClient.listBranches(form.companyId, 100, 0).then(r => {
        const d = r.data.data;
        setBranches(d?.branches ?? d?.rows ?? (Array.isArray(d) ? d : []));
      }).catch(() => setBranches([]));
    } else { setBranches([]); }
  }, [form.companyId]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const payload: any = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.branchId) delete payload.branchId;
      if (!payload.companyId) delete payload.companyId;
      if (isEdit) { await apiClient.updateUser(id!, payload); navigate('/super-admin/users'); }
      else { await apiClient.createUser(payload); navigate('/super-admin/users'); }
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to save user');
    } finally { setLoading(false); }
  };

  const needsBranch = ['COMPANY_AGENT', 'WAREHOUSE_MANAGER', 'INVENTORY_AUDITOR'].includes(form.role);

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">{isEdit ? 'Edit User' : 'New User'}</h1>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name *</label>
              <input required value={form.firstName} onChange={e => set('firstName', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input required value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input type="password" required={!isEdit} value={form.password} onChange={e => set('password', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Role *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            {form.role !== 'SUPER_ADMIN' && form.role !== 'INDEPENDENT_USER' && (
              <div className="form-group">
                <label>Company</label>
                <select value={form.companyId} onChange={e => set('companyId', e.target.value)}>
                  <option value="">— Select Company —</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {needsBranch && form.companyId && (
              <div className="form-group">
                <label>Branch</label>
                <select value={form.branchId} onChange={e => set('branchId', e.target.value)}>
                  <option value="">— Select Branch —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            {isEdit && (
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {['ACTIVE', 'INACTIVE', 'SUSPENDED'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Save size={15} /> {loading ? 'Saving...' : 'Save User'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
};
